import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Settings.css";
import useUserStore from "../../store/useUserStore";
import {
  changePassword,
  deleteAccount,
  exportUserData,
  getCurrentUserProfile,
  getNotificationSettings,
  getPricingSettings,
  getSecuritySettings,
  updateNotificationSettings,
  updatePricingSettings,
  updateSecuritySettings,
  updateUserProfile,
  uploadProfileImage,
} from "../../services/settings";
import { logoutUser } from "../../services/auth";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Bell,
  Download,
  Eye,
  EyeOff,
  Lock,
  Save,
  Shield,
  Trash2,
  Upload,
  User,
  Wallet,
} from "lucide-react";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const Settings = () => {
  const fileInputRef = useRef(null);
  const { user, setUser, clearUser } = useUserStore();

  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [showPassword, setShowPassword] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneContact: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    orderUpdates: true,
    systemAlerts: true,
    marketingEmails: false,
    weeklyReports: true,
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 30,
    loginAlerts: true,
    deviceManagement: true,
  });

  const [pricingSettings, setPricingSettings] = useState({
    vatRate: 0.1,
    serviceCharge: 0,
    otherFees: 0,
  });

  const [deleteAccountData, setDeleteAccountData] = useState({
    password: "",
    confirmation: "",
    reason: "",
  });

  const tabs = useMemo(
    () => [
      { id: "profile", label: "Profile", icon: User },
      { id: "security", label: "Security", icon: Shield },
      { id: "notifications", label: "Notifications", icon: Bell },
      { id: "pricing", label: "Pricing", icon: Wallet },
      { id: "data", label: "Data & Privacy", icon: Trash2 },
    ],
    []
  );

  const initials = `${profileData.firstName?.[0] || ""}${profileData.lastName?.[0] || ""}`
    .toUpperCase()
    .trim() || "AD";

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchUserData(), fetchSettings()]);
    };
    load();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await getCurrentUserProfile();
      const payload = response?.data ?? response ?? {};

      setProfileData({
        firstName: payload?.firstName ?? "",
        lastName: payload?.lastName ?? "",
        email: payload?.email ?? "",
        phoneContact: payload?.phoneContact ?? "",
      });

      if (payload?.profileImage) {
        setSelectedImage(payload.profileImage);
      }
    } catch (_error) {
      setError("Failed to load profile data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const [notificationsRes, securityRes, pricingRes] = await Promise.all([
        getNotificationSettings(),
        getSecuritySettings(),
        getPricingSettings(),
      ]);

      const notificationsPayload = notificationsRes?.data ?? notificationsRes ?? {};
      const securityPayload = securityRes?.data ?? securityRes ?? {};
      const pricingPayload = pricingRes?.data ?? pricingRes ?? {};

      setNotificationSettings((prev) => ({ ...prev, ...notificationsPayload }));
      setSecuritySettings((prev) => ({ ...prev, ...securityPayload }));
      setPricingSettings((prev) => ({ ...prev, ...pricingPayload }));
    } catch (_error) {
      setError("Some settings failed to load.");
    }
  };

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleProfileSave = async () => {
    try {
      resetMessages();
      setLoading(true);
      await updateUserProfile(profileData);
      if (imageFile) {
        await uploadProfileImage(imageFile);
      }
      setUser({ ...user, ...profileData });
      setSuccess("Profile updated successfully.");
    } catch (_error) {
      setError("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if ((passwordData.newPassword || "").length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    try {
      resetMessages();
      setLoading(true);
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setSuccess("Password changed successfully.");
    } catch (_error) {
      setError("Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationsSave = async () => {
    try {
      resetMessages();
      setLoading(true);
      await updateNotificationSettings(notificationSettings);
      setSuccess("Notification settings saved.");
    } catch (_error) {
      setError("Failed to save notification settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySave = async () => {
    try {
      resetMessages();
      setLoading(true);
      await updateSecuritySettings(securitySettings);
      setSuccess("Security settings saved.");
    } catch (_error) {
      setError("Failed to save security settings.");
    } finally {
      setLoading(false);
    }
  };

  const handlePricingSave = async () => {
    try {
      resetMessages();
      setLoading(true);
      await updatePricingSettings(pricingSettings);
      setSuccess("Pricing settings saved.");
    } catch (_error) {
      setError("Failed to save pricing settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      resetMessages();
      setLoading(true);
      const blob = await exportUserData();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "admin-data-export.json";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess("Data export completed.");
    } catch (_error) {
      setError("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteAccountData.confirmation !== "DELETE") {
      setError("Type DELETE to confirm account deletion.");
      return;
    }
    if (!deleteAccountData.password) {
      setError("Password is required.");
      return;
    }

    try {
      resetMessages();
      setLoading(true);
      await deleteAccount({
        password: deleteAccountData.password,
        reason: deleteAccountData.reason,
      });
      clearUser();
      logoutUser();
      window.location.href = "/login";
    } catch (_error) {
      setError("Failed to delete account.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setSelectedImage(URL.createObjectURL(file));
  };

  return (
    <div className="settings-shell">
      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
          <CardDescription>Manage account, security, notifications, and data controls.</CardDescription>
        </CardHeader>
      </Card>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action Failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {success ? (
        <Alert variant="success">
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="settings-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.id} value={tab.id}>
                <Icon size={14} />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Update your personal information and profile image.</CardDescription>
            </CardHeader>
            <CardContent className="settings-grid">
              <div className="settings-avatar-col">
                <Avatar className="settings-avatar">
                  {selectedImage ? <AvatarImage src={selectedImage} alt="Profile" /> : null}
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="settings-hidden"
                  onChange={handleImageChange}
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={14} /> Upload Photo
                </Button>
              </div>
              <div className="settings-fields">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="phoneContact">Phone Number</Label>
                  <Input
                    id="phoneContact"
                    value={profileData.phoneContact}
                    onChange={(e) => setProfileData((prev) => ({ ...prev, phoneContact: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleProfileSave} disabled={loading}>
                <Save size={14} /> {loading ? "Saving..." : "Save Profile"}
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Set a new password for this admin account.</CardDescription>
            </CardHeader>
            <CardContent className="settings-fields single-column">
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="settings-inline">
                  <Input
                    id="currentPassword"
                    type={showPassword.current ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData((prev) => ({ ...prev, currentPassword: e.target.value }))
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => ({ ...prev, current: !prev.current }))}
                  >
                    {showPassword.current ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="settings-inline">
                  <Input
                    id="newPassword"
                    type={showPassword.next ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => ({ ...prev, next: !prev.next }))}
                  >
                    {showPassword.next ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="settings-inline">
                  <Input
                    id="confirmPassword"
                    type={showPassword.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))}
                  >
                    {showPassword.confirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handlePasswordSave} disabled={loading}>
                <Lock size={14} /> {loading ? "Updating..." : "Update Password"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Preferences</CardTitle>
              <CardDescription>Adjust sign-in and session behavior.</CardDescription>
            </CardHeader>
            <CardContent className="settings-stack">
              <div className="settings-row">
                <div>
                  <Label>Two-Factor Authentication</Label>
                  <p>Require a second verification step.</p>
                </div>
                <Switch
                  checked={!!securitySettings.twoFactorAuth}
                  onCheckedChange={(checked) =>
                    setSecuritySettings((prev) => ({ ...prev, twoFactorAuth: checked }))
                  }
                />
              </div>
              <Separator />
              <div className="settings-row">
                <div>
                  <Label>Login Alerts</Label>
                  <p>Get notified on new device login.</p>
                </div>
                <Switch
                  checked={!!securitySettings.loginAlerts}
                  onCheckedChange={(checked) =>
                    setSecuritySettings((prev) => ({ ...prev, loginAlerts: checked }))
                  }
                />
              </div>
              <Separator />
              <div>
                <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  min="5"
                  max="1440"
                  value={securitySettings.sessionTimeout}
                  onChange={(e) =>
                    setSecuritySettings((prev) => ({
                      ...prev,
                      sessionTimeout: Math.max(5, Math.min(1440, toNumber(e.target.value, 30))),
                    }))
                  }
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSecuritySave} disabled={loading}>
                <Save size={14} /> {loading ? "Saving..." : "Save Security"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>Control which updates are sent to you.</CardDescription>
            </CardHeader>
            <CardContent className="settings-stack">
              {[
                ["emailNotifications", "Email Notifications", "Account and system updates via email."],
                ["pushNotifications", "Push Notifications", "Browser or device push alerts."],
                ["orderUpdates", "Order Updates", "Events about order status and activity."],
                ["systemAlerts", "System Alerts", "Important platform alerts."],
                ["marketingEmails", "Marketing Emails", "Promotions and product news."],
                ["weeklyReports", "Weekly Reports", "Weekly digest summary."],
              ].map(([key, title, description], index) => (
                <React.Fragment key={key}>
                  <div className="settings-row">
                    <div>
                      <Label>{title}</Label>
                      <p>{description}</p>
                    </div>
                    <Switch
                      checked={!!notificationSettings[key]}
                      onCheckedChange={(checked) =>
                        setNotificationSettings((prev) => ({ ...prev, [key]: checked }))
                      }
                    />
                  </div>
                  {index < 5 ? <Separator /> : null}
                </React.Fragment>
              ))}
            </CardContent>
            <CardFooter>
              <Button onClick={handleNotificationsSave} disabled={loading}>
                <Save size={14} /> {loading ? "Saving..." : "Save Notifications"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="pricing">
          <Card>
            <CardHeader>
              <CardTitle>Pricing Configuration</CardTitle>
              <CardDescription>Manage VAT and fee percentages.</CardDescription>
            </CardHeader>
            <CardContent className="settings-fields">
              <div>
                <Label htmlFor="vatRate">VAT Rate (%)</Label>
                <Input
                  id="vatRate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={(toNumber(pricingSettings.vatRate) * 100).toFixed(2)}
                  onChange={(e) =>
                    setPricingSettings((prev) => ({
                      ...prev,
                      vatRate: Math.max(0, Math.min(100, toNumber(e.target.value))) / 100,
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="serviceCharge">Service Charge (%)</Label>
                <Input
                  id="serviceCharge"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={toNumber(pricingSettings.serviceCharge)}
                  onChange={(e) =>
                    setPricingSettings((prev) => ({
                      ...prev,
                      serviceCharge: Math.max(0, Math.min(100, toNumber(e.target.value))),
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="otherFees">Other Fees (%)</Label>
                <Input
                  id="otherFees"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={toNumber(pricingSettings.otherFees)}
                  onChange={(e) =>
                    setPricingSettings((prev) => ({
                      ...prev,
                      otherFees: Math.max(0, Math.min(100, toNumber(e.target.value))),
                    }))
                  }
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handlePricingSave} disabled={loading}>
                <Save size={14} /> {loading ? "Saving..." : "Save Pricing"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="data">
          <Card>
            <CardHeader>
              <CardTitle>Data Export</CardTitle>
              <CardDescription>Download your admin account data snapshot.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={handleExportData} disabled={loading}>
                <Download size={14} /> {loading ? "Exporting..." : "Export Data"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Delete Account</CardTitle>
              <CardDescription>Permanently remove this admin account and all related access.</CardDescription>
            </CardHeader>
            <CardContent className="settings-stack">
              <div>
                <Label htmlFor="deletePassword">Password</Label>
                <Input
                  id="deletePassword"
                  type="password"
                  value={deleteAccountData.password}
                  onChange={(e) =>
                    setDeleteAccountData((prev) => ({ ...prev, password: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="deleteConfirmation">Type DELETE</Label>
                <Input
                  id="deleteConfirmation"
                  value={deleteAccountData.confirmation}
                  onChange={(e) =>
                    setDeleteAccountData((prev) => ({ ...prev, confirmation: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="deleteReason">Reason (Optional)</Label>
                <Textarea
                  id="deleteReason"
                  value={deleteAccountData.reason}
                  onChange={(e) =>
                    setDeleteAccountData((prev) => ({ ...prev, reason: e.target.value }))
                  }
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} disabled={loading}>
                <Trash2 size={14} /> Delete Account
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Account Deletion</DialogTitle>
          </DialogHeader>
          <p className="settings-confirm-text">
            This action is permanent. Type DELETE and enter your password correctly before continuing.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={loading}>
              {loading ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
